import type { JournalReportDocument, ReportRequestBody } from '@query'
import { useReportEntries } from '@query'
import React, { useEffect, useMemo, useState } from 'react'
import { JournalDocumentEntries } from './JournalDocumentEntries'
import { drillEntryParams } from './drillEntryParams'

type Props = {
  slug: string
  reportBody: ReportRequestBody
  documentNumber: string
  currency?: string
}

export const JournalDocumentEntriesPanel: React.FC<Props> = ({
  slug,
  reportBody,
  documentNumber,
  currency
}) => {
  const [page, setPage] = useState(1)

  useEffect(() => {
    setPage(1)
  }, [documentNumber])

  const params = useMemo(
    () => drillEntryParams(reportBody, { document_number: documentNumber }, page),
    [reportBody, documentNumber, page]
  )

  const { data, isFetching, isLoading } = useReportEntries({
    slug,
    params,
    enabled: Boolean(slug && documentNumber)
  })

  const doc: JournalReportDocument = useMemo(
    () => ({
      number: data?.meta.document_number ?? documentNumber,
      date: data?.results[0]?.date ?? null,
      entries: (data?.results ?? []).map(entry => ({
        account_number: entry.account_number,
        account_name: entry.account_name,
        debet: entry.debet,
        credit: entry.credit,
        description: entry.description
      })),
      entries_count: data?.count ?? 0,
      totals: {
        debet: data?.meta.debet_total ?? 0,
        credit: data?.meta.credit_total ?? 0
      }
    }),
    [data, documentNumber]
  )

  const showLoading = isLoading || isFetching

  return (
    <JournalDocumentEntries
      doc={doc}
      currency={currency}
      isLoading={showLoading}
      entriesPage={page}
      entriesPageCount={data?.page_count ?? 1}
      entriesTotalCount={data?.count ?? 0}
      entriesPageSize={data?.page_size ?? params.page_size}
      onEntriesPageChange={setPage}
    />
  )
}
